import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { loadConfiguration } from './sportsbook-duck';
import useForcePinMobileNavbar from '../../../hooks/use-force-pin-mobile-headroom';
import useInitializeBettingService from './hooks/use-initialize-betting-service';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header/index';
import {
	selectFranchiseCode,
	selectGlobalNavigationEnabled,
	selectSportsbookBetBuilderEnabled
} from '../../brand/ducks/selectors';
import { dispose as disposeConfiguration } from './sportsbook-configuration-duck';
import SportsbookDesktopMenu from './features/desktop-menu/sportsbook-desktop-menu-container';
import { isSmallerSelector } from '../../layout/mediaquery';
import useConsumeQuerySelections from './hooks/use-consume-query-selections';
import useConsumeQuerySelectionsByReference from './hooks/use-consume-query-selections-by-reference';
import useConsumeQueryApplyBonus from './hooks/use-consume-query-apply-bonus';
import { isNativeNavHeaderEnabled, isNativeBottomNavigationEnabled } from '~/components/app-bridge/app-properties';
import { BetslipContainer } from './sportsbook-async';
import { selectIsSBApiReady, selectHasSportsbookApiSetupFailed } from './api/sportsbook-api-duck';
import { selectIsReadyForApiCalls } from './sportsbook-root-selectors';
import useKeepAliveOnInteraction from '~/hooks/use-keep-alive-on-interaction';
import { AppDispatch } from '../../../store/types';
import BetBuilderModalContainer from './features/bet-builder-modal/sportsbook-bet-builder-modal-container';
import ShareBetModalContainer from './features/share-bet-modal/sportsbook-share-bet-modal-container';
import useShareBetModal from './features/share-bet-modal/sportsbook-use-share-bet-modal';
import useInitializeBettingServiceRsocketConnection from './hooks/use-initialize-betting-rsocket-connection';
import useInitializeSportsbookApi from './hooks/use-initialize-sportsbook-api';
import { setMainContentLoaded } from '../../brand/ducks';
import SportsbookApiErrorDisplay from './components/sportsbook-api-error-display';
import useSportsbookQuickDeposit from './hooks/use-sportsbook-quick-deposit';
import SportsbookRoutes from './sportsbook-routes';
import useHasLoginFooter from './hooks/use-has-login-footer';
import { reloadBalance } from './features/betslip/ducks/betslip-common-duck';
import SportsbookTopbarContainer from './features/top-bar/sportsbook-top-bar-container';
import useInitializeBetslip from './features/betslip/use-initialize-betslip';
import { ContentWrapper, Layout, Wrapper } from './sportsbook-root-layout';
import HeaderSelector from '~/components/header/header-selector';
import { getZindexByType } from '../../../utils/style-utils';
import useOpenBets from './features/open-bets/use-open-bets';

const SportsbookLoginFooterWrapper = styled.div<{ hasBottomNav: boolean }>`
	position: fixed;
	left: 0;
	right: 0;
	z-index: ${getZindexByType('sportsbook-popover-content')};
	bottom: 0;

	${({ hasBottomNav }) =>
		hasBottomNav &&
		css`
			@media (max-width: 768px) {
				bottom: var(--bottomNavHeight);
				div {
					position: absolute;
				}
			}
		`}
`;

const hasNativeHeaders = isNativeNavHeaderEnabled();

/**
 * Initialze the applications configuration state used by different features of the Sportsbook application.
 * Some of the properties returned from this request are required to use the Betslip and other features.
 */
const useInitializeConfiguration = () => {
	const dispatch: AppDispatch = useDispatch();
	const franchiseCode = useSelector(selectFranchiseCode);
	const isSBApiReady = useSelector(selectIsSBApiReady);

	useEffect(() => {
		if (isSBApiReady) {
			dispatch(loadConfiguration());
		}
	}, [isSBApiReady, franchiseCode, dispatch]);

	useEffect(
		() => () => {
			dispatch(disposeConfiguration());
		},
		[dispatch]
	);
};

/**
 * This hook will initialize all the necessary services for the Sportsbook application to function properly.
 * Some of these services are are dependant on eachother and might have dependencies to each other.
 */
const useInitializeContainer = () => {
	const dispatch: AppDispatch = useDispatch();
	const { forcePinHeadroom, unforcePinHeadroom } = useForcePinMobileNavbar();
	const isReadyForApiCalls = useSelector(selectIsReadyForApiCalls);
	const hasSportsbookApiSetupFailed = useSelector(selectHasSportsbookApiSetupFailed);
	// SBAPI
	useInitializeSportsbookApi();
	// CONFIGURATION
	useInitializeConfiguration();
	// BETS & BETTING SERVICE
	useInitializeBettingService();
	useInitializeBettingServiceRsocketConnection();

	useEffect(() => {
		forcePinHeadroom();
		return () => {
			unforcePinHeadroom();
		};
	}, [forcePinHeadroom, unforcePinHeadroom]);

	useEffect(() => {
		if (isReadyForApiCalls || hasSportsbookApiSetupFailed) {
			dispatch(setMainContentLoaded());
		}
	}, [dispatch, isReadyForApiCalls, hasSportsbookApiSetupFailed]);

	// Automatic balance updates are flaky, so manually update the balance when sportsbook loads
	useEffect(() => {
		dispatch(reloadBalance());
	}, [dispatch]);

	return [isReadyForApiCalls, hasSportsbookApiSetupFailed] as const;
};

const SportsbookContainer = () => {
	const isBetBuilderEnabled = useSelector(selectSportsbookBetBuilderEnabled);
	const hasRegulationsHeader = useSelector(selectHasRegulationsHeader);
	const isGlobalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);
	const { shouldDisplayBetslip } = useInitializeBetslip();
	const isSmaller = useSelector(isSmallerSelector);
	const hasLoginFooter = useHasLoginFooter();

	const { isShareBetModalEnabled } = useShareBetModal();

	const shouldDisplayTopBarContainer = useMemo(() => {
		return !isGlobalNavigationEnabled || !isSmaller;
	}, [isGlobalNavigationEnabled, isSmaller]);

	useKeepAliveOnInteraction();

	// Initialize sportsbook container. This will execute many tasks to setup different modules that are required by features in the Sportsbook
	const [isContainerReady, hasSportsbookApiSetupFailed] = useInitializeContainer();

	// Consume any query params that affects sportsbook globally
	useConsumeQuerySelections();
	useConsumeQuerySelectionsByReference();
	useConsumeQueryApplyBonus();

	useSportsbookQuickDeposit(true); // Ensure we check QD availability early

	// Initialize subscription to open bets and maintain it always
	useOpenBets();

	if (hasSportsbookApiSetupFailed) {
		return <SportsbookApiErrorDisplay />;
	}

	if (!isContainerReady) {
		return null;
	}

	return (
		<Wrapper
			id="sportsbook-wrapper"
			hasRegulationsHeader={hasRegulationsHeader}
			hasNativeHeaders={hasNativeHeaders}
		>
			<Layout hasGlobalNav={isGlobalNavigationEnabled}>
				{!isSmaller && <SportsbookDesktopMenu />}
				<ContentWrapper data-at="sportsbook-content-wrapper">
					{shouldDisplayTopBarContainer && <SportsbookTopbarContainer />}
					<SportsbookRoutes />
				</ContentWrapper>
			</Layout>
			{hasLoginFooter && (
				<SportsbookLoginFooterWrapper
					hasBottomNav={isGlobalNavigationEnabled && !isNativeBottomNavigationEnabled()}
				>
					<HeaderSelector />
				</SportsbookLoginFooterWrapper>
			)}
			{isBetBuilderEnabled && <BetBuilderModalContainer />}
			{isShareBetModalEnabled && <ShareBetModalContainer />}
			<div id="sportsbook-portal-root" />
			{shouldDisplayBetslip && <BetslipContainer /> /* To keep betslip over everything else, render it last */}
		</Wrapper>
	);
};

export default SportsbookContainer;
